import React, { useState, useEffect } from "react";
import MyPagination from "../../../components/pagination/Pagination";
import TableWithSkeleton from "../../../components/table/TableWithSkeleton";
import columns from "../../../columns/leaderboard";
import { GetLeaderboardApi } from "../../../api/request/leaderboard";

const Leaderboard = () => {
  const [loading, setLoading] = useState(false);
  const [topTraders, setTopTraders] = useState([]);
  const [otherTraders, setOtherTraders] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  const [searchText, setSearchText] = useState("");

  const getLeaderboard = async () => {
    try {
      setLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
        searchKeyword: searchText || "",
      };

      const { data } = await GetLeaderboardApi(
        new URLSearchParams(params).toString()
      );

      if (data?.success) {
        setTotalRecord(data.totalRecords);

        const formattedData = data?.data?.map((item) => ({
          ...item,
          profitUI: (
            <div
              className={`flex justify-center items-center rounded-lg ${
                item.profit >= 0 ? "bg-[#E5F9F0]" : "bg-[#fff0ef]"
              }`}
            >
              <p
                className={`uppercase font-semibold text-sm py-1 px-2 ${
                  item.profit >= 0 ? "text-green-600" : "text-[#EB5950]"
                }`}
              >
                {item.profit}
              </p>
            </div>
          ),
          profitPercents: (
            <p
              className={`font-semibold ${
                item.profitPercent >= 0 ? "text-[#4D9F73]" : "text-[#EB5950]"
              }`}
            >
              {item.profitPercent}%
            </p>
          ),
          winRatios: (
            <p className="font-semibold text-black">{item.winRatio}%</p>
          ),
          pair: (
            <div className="bg-[#EAECF9] flex justify-center items-center rounded-lg">
              <p className="uppercase font-semibold text-[#4D5BAF] text-sm py-1 px-2">
                {item.pair || "-"}
              </p>
            </div>
          ),
          avgWins: (
            <p className="font-semibold text-[#42BE9E]">{item.avgWin}</p>
          ),
          avgLosses: (
            <p className="font-semibold text-[#EB5950]">{item.avgLoss}</p>
          ),
          trades: <p className="font-bold">{item.trades}</p>,
          losingStreak: (
            <p className="font-semibold text-[#EB5950]">{item.losingStreak}</p>
          ),
          winningStreaks: (
            <p className="font-semibold text-[#42BE9E]">{item.winningStreak}</p>
          ),
          totalLot: <p className="font-bold">{item.totalLots}</p>,
        }));

        setTopTraders(formattedData.slice(0, 3));
        setOtherTraders(formattedData.slice(3));
      } else {
        setTopTraders([]);
        setOtherTraders([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeaderboard();
  }, [page, pageSize, searchText]);

  const SkeletonCard = () => (
    <div className="bg-white border-2 border-light rounded-xl p-5 flex flex-col gap-5 animate-pulse">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-14 bg-gray-200 rounded-full" />
        <div className="flex flex-col gap-2 w-2/3">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="ml-auto h-5 w-16 bg-gray-200 rounded" />
      </div>
      <div className="flex justify-between w-full mt-auto">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col text-center gap-2">
            <div className="h-3 w-14 bg-gray-200 rounded" />
            <div className="h-4 w-10 bg-gray-300 rounded" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {!loading && topTraders.length === 0 && otherTraders.length === 0 ? (
         <div className="flex flex-col justify-center items-center text-center h-screen pb-40">
          <img
            src="/assets/images/nodata.svg"
            alt="No data"
            className="opacity-90"
          />
          <h2 className="text-3xl font-bold text-gray-700 mb-3">
            No Leaderboard Data Yet
          </h2>
          <p className="text-gray-400 text-lg">There is no data to show you right now</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 xl:gap-6 gap-4">
            {loading
              ? [1, 2, 3].map((i) => <SkeletonCard key={i} />)
              : topTraders.map((trader, index) => (
                  <div
                    key={index}
                    className="bg-white border-2 border-light rounded-xl p-5 flex flex-col gap-5 relative"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div
                        className={`flex-shrink-0 flex justify-center items-center px-5 py-3 rounded-full ${
                          index === 0
                            ? "bg-[#dab46054]"
                            : index === 1
                            ? "bg-slate-200"
                            : "bg-[#FFF0E1]"
                        }`}
                      >
                        <img
                          src={`/assets/images/rank${index + 1}.png`}
                          className="w-8"
                          alt={`Rank ${index + 1}`}
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 text-xl mb-1 line-clamp-1">
                          {trader?.trader}
                        </h3>
                        <p className="text-sm text-gray-500">{trader?.login}</p>
                      </div>
                      <p className="ml-auto font-bold text-green-600 xl:text-2xl text-xl">
                        ${trader?.profit}
                      </p>
                    </div>

                    <div className="flex flex-wrap justify-between gap-2 mt-auto">
                      <div className="flex justify-between gap-2 w-full">
                        <div className="flex flex-col text-center">
                          <h5 className="text-sm text-gray-500 capitalize">
                            Profit %
                          </h5>
                          <span className="text-lg font-bold text-gray-800">
                            {trader?.profitPercent}
                          </span>
                        </div>
                        <div className="flex flex-col text-center">
                          <h5 className="text-sm text-gray-500 capitalize">
                            Avg Win:
                          </h5>
                          <span className="text-lg font-bold text-gray-800">
                            {trader?.avgWin}
                          </span>
                        </div>
                        <div className="flex flex-col text-center">
                          <h5 className="text-sm text-gray-500 capitalize">
                            Avg Loss:
                          </h5>
                          <span className="text-lg font-bold text-gray-800">
                            {trader?.avgLoss}
                          </span>
                        </div>
                        <div className="flex flex-col text-center">
                          <h5 className="text-sm text-gray-500 capitalize">
                            Total Lots:
                          </h5>
                          <span className="text-lg font-bold text-gray-800">
                            {trader?.totalLots}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          <div className="border border-gray-200 rounded-xl bg-white mt-5">
            <TableWithSkeleton
              columns={columns}
              data={otherTraders}
              loading={loading}
              pagination={false}
            />
          </div>

          <MyPagination
            total={totalRecord}
            pageSize={pageSize}
            setPageSize={setPageSize}
            page={page}
            setPage={setPage}
          />
        </>
      )}
    </>
  );
};

export default Leaderboard;
